import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";

const PARAGRAPHS = [
  "Я в эксперименте с Human Design с 2023 года. После получения своего личного прочтения я впервые почувствовала настоящее облегчение и ясность. С этого момента начался мой собственный путь познания себя.",
  "В моей жизни много ролей: я руководитель в крупной международной корпорации, дочь, жена и мама. Моя жизнь стала меняться. Я осталась в тех же ролях, но начала позволять себе быть собой.",
  "Мой путь познания себя был долгим и включал переезды в разные города и даже страны. Постепенно я стала лучше понимать себя, свои особенности и то, как мне комфортно проживать свою жизнь.",
  "Теперь я сама обучаюсь в IHDS — Международной Школе Дизайна Человека. Я успешно завершила три курса: Living Your Design, Rave ABC и Rave Cartography — и продолжаю своё обучение.",
];

const CERTS = [
  {
    name: "Living Your Design",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_olga-clarity/artifacts/f7tcrucl_IMG_9254.jpeg",
  },
  {
    name: "Rave ABC",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_olga-clarity/artifacts/qz823ipt_IMG_9253.jpeg",
  },
  {
    name: "Rave Cartography",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_olga-clarity/artifacts/mtsb7bxc_IMG_9252.jpeg",
  },
];

export const About = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
  <section id="about" data-testid="about-section" className="bg-beige/50 py-28 md:py-40">
    <div className="mx-auto max-w-[1240px] px-6 md:px-10">
      <div className="grid gap-14 md:grid-cols-[0.85fr_1.15fr] md:gap-20">
        <div className="md:sticky md:top-28 md:self-start">
          <Reveal>
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_olga-clarity/artifacts/of02o3rw_Olga0173_%282%29%201%201.webp"
              alt="Ольга Щербич"
              data-testid="about-portrait"
              className="aspect-[4/5] w-full object-cover object-top"
            />
          </Reveal>
        </div>

        <div>
          <Reveal>
            <p className="font-sans text-xs uppercase tracking-[0.32em] text-sage">Обо мне</p>
            <h2 className="mt-6 font-serif text-[2.4rem] tracking-tight text-charcoal md:text-[3rem]">
              Я — Проектор 5/1.
            </h2>
          </Reveal>

          <div className="mt-10 max-w-xl space-y-6 font-sans text-base font-light leading-relaxed text-charcoal/75">
            {PARAGRAPHS.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <blockquote
              data-testid="about-closing-quote"
              className="mt-14 max-w-xl border-l-2 border-sage pl-6 font-serif text-3xl italic leading-snug tracking-tight text-charcoal md:text-4xl"
            >
              Как Проектор, я буду рада подсветить Ваш путь.
            </blockquote>
          </Reveal>

          <div className="mt-20" id="ihds">
            <Reveal>
              <p className="font-sans text-xs uppercase tracking-[0.32em] text-sage">
                Моё обучение в IHDS
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-8 grid gap-6 sm:grid-cols-3">
                {CERTS.map((c) => (
                  <div key={c.name} data-testid={`cert-${c.name.split(" ")[0].toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => setLightbox(c)}
                      aria-label={`Открыть сертификат ${c.name}`}
                      data-testid={`cert-open-${c.name.split(" ")[0].toLowerCase()}`}
                      className="group block w-full cursor-zoom-in"
                    >
                      <img
                        src={c.img}
                        alt={`Сертификат IHDS — ${c.name}`}
                        className="w-full border border-charcoal/10 transition-[transform,box-shadow] duration-300 group-hover:scale-[1.02] group-hover:shadow-lg"
                      />
                    </button>
                    <p className="mt-3 text-center font-sans text-sm text-charcoal/70">{c.name}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-center font-sans text-xs text-charcoal/40 sm:text-left">
                Нажмите на сертификат, чтобы рассмотреть его ближе.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </div>

    <AnimatePresence>
      {lightbox && (
        <motion.div
          data-testid="cert-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-charcoal/85 p-6 backdrop-blur-sm md:p-12"
        >
          <button
            type="button"
            aria-label="Закрыть"
            data-testid="cert-lightbox-close"
            className="absolute right-5 top-5 text-ivory/80 transition-colors hover:text-ivory md:right-8 md:top-8"
          >
            <X className="h-7 w-7" strokeWidth={1.4} />
          </button>
          <motion.img
            src={lightbox.img}
            alt={`Сертификат IHDS — ${lightbox.name}`}
            initial={{ scale: 0.96, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[82vh] max-w-[92vw] object-contain shadow-2xl"
          />
          <p className="mt-5 font-sans text-sm text-ivory/70">{lightbox.name}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};
